/*
 * -----------------------------------------------------------------------
 *  <copyright file="contractual-information-dialog.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormControl from '@mui/material/FormControl';
import moment from 'moment';

import { useBoolean, useTranslation } from 'hooks';
import { StudyFields } from 'queries/generated/fragments.generated';
import { GET_STUDY, SAVE_STUDY_CONTRACTUAL_INFORMATION } from 'queries/studies';
import { formatDateUTC_DEPRECATED } from 'utils/convertor';
import NotificationService from 'utils/notification';
import { SaveButton } from 'views/components/core/buttons';
import DatePicker from 'views/components/core/date-picker';

import 'views/components/admin/studies/study-settings-dialogs/study-settings-dialogs.scss';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';

const ContractualInformationDialog = ({
    study,
    open,
    onClose,
}: {
    study: StudyFields;
    open: boolean;
    onClose: Function;
}) => {
    const { t } = useTranslation();
    const [saveStudyContractualInformation] = useMutation(SAVE_STUDY_CONTRACTUAL_INFORMATION);
    const disableButton = useBoolean(true);
    const initialState = {
        RFPStartDate: null as any,
        ISMPStartDate: null as any,
        CMFirstDataLoadDate: null as any,
        AwardStartDate: null as any,
        CMReleaseDate: null as any,
    };
    const [state, setState] = useState({ ...initialState });

    useEffect(() => {
        if (open)
            setState({
                RFPStartDate:
                    moment(study.riskAssessmentRfpStartDate).year() > 1
                        ? formatDateUTC_DEPRECATED(study.riskAssessmentRfpStartDate, true)
                        : null,
                ISMPStartDate:
                    moment(study.riskAssessmentIsmpStartDate).year() > 1
                        ? formatDateUTC_DEPRECATED(study.riskAssessmentIsmpStartDate, true)
                        : null,
                CMFirstDataLoadDate:
                    moment(study.centralMonitoringFirstDataLoadDate).year() > 1
                        ? formatDateUTC_DEPRECATED(study.centralMonitoringFirstDataLoadDate, true)
                        : null,
                AwardStartDate:
                    moment(study.riskAssessmentAwardStartDate).year() > 1
                        ? formatDateUTC_DEPRECATED(study.riskAssessmentAwardStartDate, true)
                        : null,
                CMReleaseDate:
                    moment(study.centralMonitoringReleaseDate).year() > 1
                        ? formatDateUTC_DEPRECATED(study.centralMonitoringReleaseDate, true)
                        : null,
            });
    }, [study, open]);

    const close = (value?: boolean) => {
        disableButton.setTrue();
        setState({ ...initialState });
        onClose(value);
    };

    const onDateChange = (name: string) => (event: any) => {
        disableButton.setFalse();

        setState({
            ...state,
            [name]: event?.value ? moment(event?.value) : null,
        });
    };

    const errorRequest = () => {
        NotificationService.showNotification(t('errorMessage'));
        disableButton.setFalse();
    };

    const successRequest = () => {
        NotificationService.showNotification(t('admin:studySuccessfulUpdated', { id: study.recordId }));
        close(true);
    };

    const onUpdate = () => {
        disableButton.setTrue();

        saveStudyContractualInformation({
            variables: {
                studyId: study.id,
                riskAssessmentRfpStartDate: formatDateUTC_DEPRECATED(state.RFPStartDate),
                riskAssessmentIsmpStartDate: formatDateUTC_DEPRECATED(state.ISMPStartDate),
                centralMonitoringFirstDataLoadDate: formatDateUTC_DEPRECATED(state.CMFirstDataLoadDate),
                riskAssessmentAwardStartDate: formatDateUTC_DEPRECATED(state.AwardStartDate),
                centralMonitoringReleaseDate: formatDateUTC_DEPRECATED(state.CMReleaseDate),
            },
            refetchQueries: [{ query: GET_STUDY, variables: { id: study.id } }],
        })
            .then(() => {
                successRequest();
            })
            .catch(() => {
                errorRequest();
            });
    };

    return (
        <CustomDialog
            className="contractual-info-dialog"
            onClose={() => close()}
            fullWidth
            scroll="paper"
            maxWidth="md"
            open={open}
        >
            <DialogTitle id="contractual-info-title">
                {t('admin:editContractualInformation')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="contractual-info-dialog-content">
                <div className="dates-wrap">
                    <FormControl variant="outlined" className="form-control-date">
                        <DatePicker
                            label={t('admin:RFPStartDate')}
                            customId="rfp-start-date"
                            selectedDate={state.RFPStartDate}
                            handleDateChange={onDateChange('RFPStartDate')}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-date">
                        <DatePicker
                            label={t('admin:ISMPStartDate')}
                            customId="ismp-start-date"
                            selectedDate={state.ISMPStartDate}
                            handleDateChange={onDateChange('ISMPStartDate')}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-date">
                        <DatePicker
                            label={t('admin:CMFirstDataLoadDate')}
                            customId="cm-first-load-date"
                            selectedDate={state.CMFirstDataLoadDate}
                            handleDateChange={onDateChange('CMFirstDataLoadDate')}
                        />
                    </FormControl>
                </div>
                <div className="dates-wrap">
                    <FormControl variant="outlined" className="form-control-date">
                        <DatePicker
                            label={t('admin:RAMAwardStartDate')}
                            customId="award-start-date"
                            selectedDate={state.AwardStartDate}
                            handleDateChange={onDateChange('AwardStartDate')}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-date">
                        <DatePicker
                            label={t('admin:CMReleaseDate')}
                            customId="cm-release-date"
                            selectedDate={state.CMReleaseDate}
                            handleDateChange={onDateChange('CMReleaseDate')}
                        />
                    </FormControl>
                </div>
            </DialogContent>
            <DialogActions className="contractual-info-dialog-actions dialog-actions flex-end">
                <SaveButton disabled={disableButton.value} text="common:update" onClick={onUpdate} />
            </DialogActions>
        </CustomDialog>
    );
};

export default ContractualInformationDialog;
